/**
 * Reducer pour le jeu de Tarot
 * 
 * Traite les actions une par une et modifie l'état du jeu de manière immutable.
 * Chaque action représente un événement du jeu (carte jouée, enchère, fin de pli, etc.)
 */

import { CardNumber, Contract } from '../types/game';
import { getCardLogName } from '../utils/cards';
import { log } from '../utils/logger';
import {
  GameState,
  GameAction,
  DistribueData,
  CarteData,
  FinPliData,
  ContratData,
  AppelData,
  Player,
  DeclaredPoignee,
} from './gameActions';
import { resolveCalledCard } from './kingCall';
import { resolveLiveDealTimeoutSeconds } from './liveDealTimeouts';

/**
 * Calcule le gagnant du pli en cours
 * Règles du Tarot:
 * - L'atout le plus fort gagne
 * - Sinon, la carte la plus forte de la couleur demandée gagne
 * - L'excuse ne peut jamais gagner un pli
 */
export function findTrickWinner(state: GameState): number {
  const { currentTrick, trickPlayerPositions, entameur } = state;
  
  if (currentTrick.length === 0 || trickPlayerPositions.length === 0) {
    return entameur ?? 0;
  }
  
  // Couleur demandée (première carte sauf excuse)
  let leadCard = currentTrick[0];
  let leadIndex = 0;
  
  // Si la première carte est l'excuse, prendre la suivante comme couleur demandée
  if (leadCard === 78) {
    for (let i = 1; i < currentTrick.length; i++) {
      if (currentTrick[i] !== 78) {
        leadCard = currentTrick[i];
        leadIndex = i;
        break;
      }
    }
  }
  
  // Déterminer la couleur demandée (0-3: couleurs, 4: atout)
  const getCardSuit = (card: CardNumber): number => {
    if (card === 78) return -1; // Excuse
    if (card >= 57 && card <= 77) return 4; // Atout
    return Math.floor((card - 1) / 14); // Couleurs 0-3
  };
  
  const getCardValue = (card: CardNumber): number => {
    if (card === 78) return 0; // Excuse - valeur 0, ne peut pas gagner
    if (card >= 57 && card <= 77) return card - 56; // Atout: 1-21
    return ((card - 1) % 14) + 1; // Couleur: 1-14
  };
  
  const leadSuit = getCardSuit(leadCard);
  
  let winnerIndex = leadIndex;
  let winnerCard = leadCard;
  let highestAtout = leadSuit === 4 ? getCardValue(leadCard) : -1;
  let highestLead = leadSuit !== 4 ? getCardValue(leadCard) : -1;
  
  for (let i = 0; i < currentTrick.length; i++) {
    if (i === leadIndex) continue;
    
    const card = currentTrick[i];
    const cardSuit = getCardSuit(card);
    const cardValue = getCardValue(card);
    
    // L'atout bat tout (sauf un atout plus fort)
    if (cardSuit === 4) {
      if (cardValue > highestAtout) {
        highestAtout = cardValue;
        winnerIndex = i;
        winnerCard = card;
      }
    } 
    // Si pas d'atout joué et même couleur que demandée
    else if (highestAtout === -1 && cardSuit === leadSuit) {
      if (cardValue > highestLead) {
        highestLead = cardValue;
        winnerIndex = i;
        winnerCard = card;
      }
    }
    // L'excuse et les autres couleurs ne peuvent pas gagner
  }
  
  const winner = trickPlayerPositions[winnerIndex];
  log.debug(`[GameReducer] Trick winner calculated: player ${winner} with card ${winnerCard}(${getCardLogName(winnerCard as CardNumber)})`);
  
  return winner;
}

/**
 * Applique une action à l'état du jeu et retourne le nouvel état
 */
export function gameReducer(state: GameState, action: GameAction): GameState {
  // Les délais et actions de timing ne modifient pas l'état - ils sont gérés par le système de queue
  if (action.type === 'delay' || action.type === 'bid_pre_delay') {
    return state;
  }
  
  log.debug(`[GameReducer] Processing action: ${action.type}`, action);
  
  switch (action.type) {
    case 'distribue':
      log.debug('📢 [GameReducer] *** DISTRIBUE ACTION PROCESSING ***');
      return handleDistribue(state, action.data);
      
    case 'carte':
      return handleCarte(state, action.data);
      
    case 'fin_pli':
      return handleFinPli(state, action.data);
      
    case 'contrat':
      return handleContrat(state, action.data);
      
    case 'bid_advance':
      return handleBidAdvance(state);
      
    case 'chien': {
      // Le message chien peut contenir le preneur et le contrat
      let chienPreneur = action.data.preneur !== undefined ? action.data.preneur : state.taker;
      let chienContrat = action.data.contrat !== undefined ? action.data.contrat as Contract : state.currentContract;
      
      // Si le preneur n'est pas défini, essayer de le calculer à partir des enchères des joueurs
      if (chienPreneur === undefined || chienPreneur === null) {
        let bestBid = 0;
        for (let i = 0; i < state.players.length; i++) {
          const playerBid = state.players[i]?.lastBid;
          if (playerBid !== undefined && playerBid > bestBid) {
            bestBid = playerBid;
            chienPreneur = i;
            chienContrat = playerBid as Contract;
          }
        }
        log.debug(`[GameReducer] chien: calculated taker from bids: preneur=${chienPreneur}, contrat=${chienContrat}`);
      }
      
      log.debug(`[GameReducer] chien: preneur=${chienPreneur}, contrat=${chienContrat}, state.taker was ${state.taker}, state.contract was ${state.currentContract}`);

      // 5 joueurs : les défenseurs voient le roi appelé avant le chien
      if (state.playerCount === 5 && state.kingCallRevealPending) {
        log.debug('[GameReducer] chien held until king call reveal dismissed');
        return {
          ...state,
          pendingChienReveal: action.data.cartes as CardNumber[],
          pendingChienMeta: { preneur: chienPreneur, contrat: chienContrat },
          phase: 3,
        };
      }

      return {
        ...state,
        chien: action.data.cartes as CardNumber[],
        taker: chienPreneur,
        currentContract: chienContrat,
        phase: 4, // ViewChien
        awaitingChienDismiss: true,
      };
    }
    
    case 'king_call_reveal_done': {
      if (!state.kingCallRevealPending) return state;
      log.debug('[GameReducer] king call reveal dismissed -> show chien');
      const meta = state.pendingChienMeta;
      return {
        ...state,
        kingCallRevealPending: false,
        phase: 4,
        chien: state.pendingChienReveal ?? state.chien,
        taker: meta?.preneur ?? state.taker,
        currentContract: meta?.contrat ?? state.currentContract,
        pendingChienReveal: undefined,
        pendingChienMeta: undefined,
        awaitingChienDismiss: true,
      };
    }
    
    case 'chien_vu': {
      // Révélation du roi encore affichée : le chien n'a pas encore été montré
      if (state.kingCallRevealPending) {
        log.debug('[GameReducer] chien_vu skipped: waiting for king call reveal');
        return state;
      }
      if (state.phase !== 4) {
        log.debug('[GameReducer] chien_vu skipped: not in chien phase', state.phase);
        return state;
      }
      // Après avoir vu le chien, déterminer la phase suivante
      const chienVuContract = state.currentContract ?? 0;
      const iAmTaker = state.taker === state.myPosition;
      const allPassed = state.taker === undefined || state.taker === null;
      
      log.debug(`[GameReducer] chien_vu: contract=${chienVuContract}, taker=${state.taker}, myPos=${state.myPosition}, iAmTaker=${iAmTaker}, allPassed=${allPassed}`);
      
      // Cas spécial : tout le monde a passé -> fin de donne
      if (allPassed) {
        log.debug('[GameReducer] All passed -> phase 99999 (Finished)');
        return {
          ...state,
          phase: 99999, // Finished - permet d'afficher le bouton "Prêt"
          chien: undefined,
          awaitingChienDismiss: false,
          ecartArrivedDuringChien: false,
          scores: {
            contratRealise: false,
            nbBouts: 0,
            pointsAttaque: 0,
            difference: 0,
          },
        };
      }
      
      // Garde Sans ou Garde Contre (>=3) -> passer directement au jeu
      if (chienVuContract >= 3) {
        log.debug('[GameReducer] Garde Sans/Contre -> phase 6 (Playing)');
        const base = {
          ...state,
          phase: 6 as number, // Playing
          chien: undefined,
          awaitingChienDismiss: false,
          ecartArrivedDuringChien: false,
        };
        return base;
      }
      
      // Petite ou Garde : passer à l'écart
      // Ajouter les cartes du chien à ma main SEULEMENT si je suis le preneur
      // Skip cards already in hand (Neo mode sends hand_update before chien_vu)
      const chienToAdd = (iAmTaker && state.chien)
        ? state.chien.filter(c => !state.myHand.includes(c))
        : [];
      const newHandAfterChien = chienToAdd.length > 0
        ? [...state.myHand, ...chienToAdd]
        : state.myHand;
      
      if (iAmTaker) {
        log.debug(`[GameReducer] I am taker -> phase 5 (MakeEcart), hand now ${newHandAfterChien.length} cards`);
      } else {
        log.debug(`[GameReducer] Waiting for taker's écart -> phase 5`);
      }
      
      const skipToPlayAfterChien = state.ecartArrivedDuringChien && !iAmTaker;

      return {
        ...state,
        myHand: newHandAfterChien,
        phase: skipToPlayAfterChien ? 6 : 5,
        chien: undefined,
        awaitingChienDismiss: false,
        ecartArrivedDuringChien: false,
        currentPlayer: skipToPlayAfterChien
          ? (state.dealer + 1) % state.playerCount
          : state.currentPlayer,
      };
    }
      
    case 'ecart': {
      if (state.awaitingChienDismiss || state.phase === 4) {
        log.debug('[GameReducer] ecart deferred: chien still visible');
        return { ...state, ecartArrivedDuringChien: true };
      }
      // Écart d'un autre joueur (message du serveur)
      // Le premier joueur est celui après le donneur
      const firstPlayer = (state.dealer + 1) % state.playerCount;
      log.debug('[GameReducer] ecart: received from server, moving to phase 6, firstPlayer:', firstPlayer);
      return {
        ...state,
        phase: 6, // Playing - après l'écart, on passe au jeu
        chien: undefined,
        currentPlayer: firstPlayer,
      };
    }
    
    case 'ecart_done': {
      // Le joueur humain a validé son écart - retirer les cartes de la main
      const ecartedCards = action.data.cartes;
      const newHandAfterEcart = state.myHand.filter(c => !ecartedCards.includes(c));
      // Le premier joueur est celui après le donneur
      const firstPlayerAfterEcart = (state.dealer + 1) % state.playerCount;
      log.debug(`[GameReducer] ecart_done: removed ${ecartedCards.length} cards, hand now ${newHandAfterEcart.length} cards, firstPlayer: ${firstPlayerAfterEcart}`);
      return {
        ...state,
        myHand: newHandAfterEcart,
        phase: 6, // Playing - après l'écart, on passe au jeu
        chien: undefined,
        currentPlayer: firstPlayerAfterEcart,
      };
    }
      
    case 'poignee_announce':
      // Afficher une poignée déclarée par un joueur
      log.debug(`[GameReducer] Poignée announced: player=${action.data.player}, type=${action.data.type}, cards=${action.data.cards.length}`);
      return {
        ...state,
        declaredPoignee: action.data as DeclaredPoignee,
      };
      
    case 'poignee_hide':
      // Masquer la poignée affichée
      log.debug('[GameReducer] Poignée hidden');
      return {
        ...state,
        declaredPoignee: undefined,
      };
      
    case 'appel':
      return handleAppel(state, action.data);
      
    case 'score': {
      log.debug('[GameReducer] score:', action.data, 'current phase:', state.phase, 'taker:', state.taker, 'numPli:', state.numPli);
      
      const isAllPassed = action.data.allPassed === true;
      const inPlayingPhase = state.phase === 6;
      const hasPlayedCards = state.numPli > 0 || state.currentTrick.length > 0;
      
      // Accept score if: playing phase with cards played, OR all-passed (from Neo)
      if (!isAllPassed && (!inPlayingPhase || !hasPlayedCards)) {
        log.debug('[GameReducer] score: ignoring score', { inPlayingPhase, hasPlayedCards, phase: state.phase });
        return state;
      }
      
      return {
        ...state,
        phase: 99999,
        scores: action.data,
      };
    }
      
    case 'joueurs':
      return handleJoueurs(state, action.data);
      
    case 'infos_table':
      return handleInfosTable(state, action.data);
      
    case 'set_position':
      return {
        ...state,
        myPosition: action.position,
      };

    case 'hand_update':
      return {
        ...state,
        myHand: action.hand,
      };

    case 'neo_waiting_lobby': {
      const players: Player[] = action.data.players
        .sort((a, b) => a.position - b.position)
        .map(p => ({
          name: p.pseudo,
          position: p.position,
          isConnected: p.isBot || p.isConnected,
        }));
      return {
        ...state,
        players,
        playerCount: players.length,
        phase: 1,
        neoAdminPseudo: action.data.adminPseudo,
        neoWaitingLobby: true,
      };
    }
      
    default:
      log.warn(`[GameReducer] Unknown action type:`, action);
      return state;
  }
}

function resolveDealTimeoutSeconds(
  tempsDonne: number | undefined,
  isNewDeal: boolean,
  state: GameState,
  data: { ip?: string; port?: number; contratObligatoire?: number },
): number | undefined {
  if (tempsDonne && tempsDonne > 0) return tempsDonne;
  const tableId = data.ip && data.port ? `${data.ip}:${data.port}` : state.tableId;
  const fallback = resolveLiveDealTimeoutSeconds({
    tableId,
    port: data.port,
    contratObligatoire: data.contratObligatoire,
  });
  if (fallback) return fallback;
  return isNewDeal ? undefined : state.dealTimeoutSeconds;
}

/**
 * Traite le message 'distribue' qui initialise/met à jour l'état du jeu
 */
function handleDistribue(state: GameState, data: DistribueData): GameState {
  const {
    donneur, cartes, contrats, etat, numPli, pliEnCours, entame, chien, idPartie, ip, port,
    contratObligatoire, syncCurrentPlayer, trickPlayerPositions: syncTrickPositions,
    tempsDonne,
  } = data;
  
  log.debug('[GameReducer] handleDistribue ENTRY:', {
    currentPhase: state.phase,
    currentScores: state.scores !== undefined,
    etat,
    contrats,
    cartesCount: cartes?.length,
  });
  
  // Déterminer le nombre de joueurs
  const playerCount = contrats.length > 0 ? contrats.length : state.playerCount || 4;
  
  // Convertir l'état serveur en phase frontend
  // États serveur: 0=rien, 1=contrat (enchères), 2=passe, 3=appel roi, 4=écart, 5=plis, 6=fin
  let phase: number;
  switch (etat) {
    case 0: phase = 2; break; // Rien -> Bidding
    case 1: phase = 2; break; // Contrat -> Bidding
    case 2: phase = 99999; break; // Passe -> Game over (tous passé)
    case 3: phase = 3; break; // Appel roi (5 joueurs)
    case 4: phase = 5; break; // Écart -> MakeEcart
    case 5: phase = 6; break; // Plis -> Playing
    case 6: phase = 99999; break; // Fin -> Game finished
    default: phase = 2; break;
  }
  
  // Détecter si c'est une NOUVELLE donne
  // Cas 1: on vient de la phase fin de partie (99999)
  // Cas 2: les cartes sont différentes de la main actuelle (nouvelle distribution)
  // Cas 3: on a des scores affichés (fin de partie précédente)
  const handChanged = cartes.length > 0 && (
    state.myHand.length !== cartes.length ||
    !cartes.every((c, i) => state.myHand[i] === c)
  );
  const incomingIdPartie = idPartie !== undefined ? String(idPartie) : undefined;
  const gameIdChanged = incomingIdPartie !== undefined && incomingIdPartie !== state.idPartie;
  const isNewDeal = gameIdChanged || state.phase === 99999 || state.scores !== undefined || (handChanged && etat <= 1);
  
  if (isNewDeal) {
    log.debug('[GameReducer] New deal detected:', { 
      wasPhase99999: state.phase === 99999, 
      hadScores: state.scores !== undefined,
      handChanged,
      etat,
      gameIdChanged,
      idPartie: incomingIdPartie,
    });
  }
  
  // Si on est DÉJÀ en phase de jeu (6) et pas une nouvelle donne, on préserve l'état local
  // Le message distribue du serveur peut arriver avec des données obsolètes
  if (state.phase === 6 && !isNewDeal) {
    log.debug('[GameReducer] Already in playing phase, preserving local state');
    const trickCards = (pliEnCours || [])
      .filter(c => c !== 0)
      .map(c => c as CardNumber);
    const trickPositions = syncTrickPositions
      ?? (trickCards.length > 0 && entame !== undefined
        ? trickCards.map((_, i) => (entame + i) % playerCount)
        : state.trickPlayerPositions);
    return {
      ...state,
      myHand: cartes as CardNumber[],
      numPli: Math.max(state.numPli, numPli || 0),
      // Reprise / resync : appliquer le joueur courant et le pli même en phase 6
      ...(syncCurrentPlayer !== undefined ? {
        currentPlayer: syncCurrentPlayer,
        currentTrick: trickCards.length > 0 ? trickCards : state.currentTrick,
        trickPlayerPositions: trickPositions.length > 0 ? trickPositions : state.trickPlayerPositions,
        ...(entame !== undefined ? { entameur: entame } : {}),
      } : {}),
    };
  }
  
  // Trouver le preneur (celui qui a le contrat le plus élevé)
  let taker: number | undefined;
  let currentContract = 0;
  for (let i = 0; i < contrats.length; i++) {
    if (contrats[i] > currentContract) {
      currentContract = contrats[i];
      taker = i;
    }
  }
  
  // Calculer qui doit jouer
  let currentPlayer = (donneur + 1) % playerCount;
  
  if (etat === 1) {
    // Enchères: trouver qui n'a pas encore parlé
    for (let i = 1; i <= playerCount; i++) {
      const pos = (donneur + i) % playerCount;
      if (contrats[pos] === -1) {
        currentPlayer = pos;
        break;
      }
    }
  } else if (etat === 3) {
    // Appel du roi (5 joueurs)
    currentPlayer = taker ?? 0;
  } else if (etat === 4) {
    // Écart: c'est au preneur
    currentPlayer = taker ?? 0;
  } else if (etat === 5) {
    // Jeu: calculer à partir de l'entame et des cartes jouées
    if (syncCurrentPlayer !== undefined) {
      currentPlayer = syncCurrentPlayer;
    } else if (pliEnCours && pliEnCours.length > 0) {
      const nbCartesJouees = pliEnCours.filter(c => c !== 0).length;
      const entameur = entame ?? (donneur + 1) % playerCount;
      currentPlayer = (entameur + nbCartesJouees) % playerCount;
    } else {
      // Premier pli ou nouveau pli
      currentPlayer = entame ?? (donneur + 1) % playerCount;
    }
  }
  
  // Récupérer les cartes du pli en cours (pour reconnexion)
  const trickCards = (pliEnCours || [])
    .filter(c => c !== 0)
    .map(c => c as CardNumber);
  const trickPositions = syncTrickPositions
    ?? (trickCards.length > 0 && entame !== undefined
      ? trickCards.map((_, i) => (entame + i) % playerCount)
      : []);
  
  // Mettre à jour les joueurs avec les enchères
  // Si c'est une nouvelle donne, réinitialiser les enchères précédentes
  const players = state.players.length > 0 ? [...state.players] : [];
  for (let i = 0; i < playerCount; i++) {
    if (!players[i]) {
      players[i] = {
        name: `Joueur ${i}`,
        position: i,
        isConnected: true,
      };
    }
    
    // Réinitialiser les données de la donne précédente si nouvelle donne
    if (isNewDeal) {
      players[i] = {
        ...players[i],
        lastBid: undefined,
        tricksWon: 0,
        isPartner: false,
      };
    }
    
    // Appliquer les contrats de la nouvelle donne
    if (contrats[i] !== undefined && contrats[i] >= 0) {
      players[i] = {
        ...players[i],
        lastBid: contrats[i] as Contract,
      };
    }
  }
  
  log.debug('[GameReducer] distribue:', {
    phase, donneur, playerCount, currentPlayer, taker, currentContract,
    numPli, trickCards: trickCards.length, isNewDeal, etat,
  });
  
  // Si on reçoit des cartes, c'est une nouvelle distribution -> vider les scores
  // Exception: si etat=2 (tout le monde a passé DANS CETTE PARTIE), on garde le message "tout le monde a passé"
  // que chien_vu va créer
  const hasNewCards = cartes && cartes.length > 0;
  const shouldClearScores = hasNewCards && (etat !== 2 || state.phase === 99999);
  
  log.debug('[GameReducer] handleDistribue scores decision:', { 
    hasNewCards, etat, statePhase: state.phase, shouldClearScores, isNewDeal 
  });
  
  // Calculer les contraintes d'enchères depuis contratObligatoire
  // (logique portée du client legacy v2 core-and-debug.js lignes 531-543)
  let bidConstraints: { min: number; max: number } | undefined;
  const cO = contratObligatoire !== undefined ? contratObligatoire : -1;
  if (cO >= 0 && cO <= 4) {
    // Contrat forcé : min=cO, max=cO (une seule option)
    bidConstraints = { min: cO, max: cO };
    log.debug('[GameReducer] Forced bid constraint:', bidConstraints);
  } else if (cO >= 7 && cO <= 10) {
    // Contrat minimum forcé : min=11-cO, max=4
    bidConstraints = { min: 11 - cO, max: 4 };
    log.debug('[GameReducer] Minimum bid constraint:', bidConstraints);
  }
  // cO === -1 : enchères libres, pas de contraintes
  
  return {
    ...state,
    tableId: ip && port ? `${ip}:${port}` : state.tableId,
    idPartie: incomingIdPartie ?? state.idPartie,
    phase,
    playerCount,
    players,
    dealer: donneur,
    currentPlayer,
    taker,
    currentContract: currentContract > 0 ? currentContract as Contract : undefined,
    myHand: cartes as CardNumber[],
    currentTrick: trickCards,
    trickPlayerPositions: trickPositions,
    chien: chien as CardNumber[] | undefined,
    numPli: numPli || 0,
    entameur: entame,
    bidConstraints,
    // Réinitialiser les données de fin de partie
    scores: shouldClearScores ? undefined : state.scores,
    lastTrick: isNewDeal ? [] : state.lastTrick,
    // Réinitialiser la poignée
    declaredPoignee: isNewDeal ? undefined : state.declaredPoignee,
    calledKing: isNewDeal ? undefined : state.calledKing,
    partner: isNewDeal ? undefined : state.partner,
    partnerRevealed: isNewDeal ? false : state.partnerRevealed,
    kingCallRevealPending: isNewDeal ? false : state.kingCallRevealPending,
    pendingChienReveal: isNewDeal ? undefined : state.pendingChienReveal,
    pendingChienMeta: isNewDeal ? undefined : state.pendingChienMeta,
    awaitingChienDismiss: isNewDeal || phase !== 4 ? false : state.awaitingChienDismiss,
    ecartArrivedDuringChien: isNewDeal || phase !== 4 ? false : state.ecartArrivedDuringChien,
    neoWaitingLobby: false,
    dealTimeoutSeconds: resolveDealTimeoutSeconds(
      tempsDonne,
      isNewDeal,
      state,
      { ip, port, contratObligatoire },
    ),
    // Nouveau départ : applyActionWithTurnTracking pose dealStartedAt sur receivedAt
    dealStartedAt: isNewDeal ? undefined : state.dealStartedAt,
  };
}

/**
 * Traite une carte jouée
 */
function handleCarte(state: GameState, data: CarteData): GameState {
  const cardPlayer = Number(data.joueur);
  const cardPlayed = Number(data.carte) as CardNumber;
  
  log.debug(`[GameReducer] Card played: player=${cardPlayer}, card=${cardPlayed}(${getCardLogName(cardPlayed)})`);
  
  // Vérifier si la carte ou le joueur est déjà dans le pli (éviter les doublons après reprise)
  if (state.trickPlayerPositions.includes(cardPlayer)) {
    log.debug('[GameReducer] Player already in trick, skipping');
    return state;
  }
  if (state.currentTrick.includes(cardPlayed)) {
    log.debug('[GameReducer] Card already in trick, skipping');
    return state;
  }
  
  // Déterminer l'entameur
  const isFirstCardOfTrick = state.currentTrick.length === 0;
  const entameur = isFirstCardOfTrick ? cardPlayer : state.entameur;
  
  // Ajouter la carte au pli
  const newTrick = [...state.currentTrick, cardPlayed];
  const newPositions = [...state.trickPlayerPositions, cardPlayer];
  
  // Retirer la carte de ma main (si c'est moi qui l'ai jouée)
  const newHand = state.myHand.filter(c => c !== cardPlayed);
  
  // Calculer le prochain joueur
  const nextPlayer = (cardPlayer + 1) % state.playerCount;
  
  // Vérifier si le pli est complet
  const trickIsComplete = newTrick.length >= state.playerCount;
  
  log.debug(`[GameReducer] Trick now has ${newTrick.length}/${state.playerCount} cards, complete=${trickIsComplete}, next player: ${nextPlayer}`);

  let partner = state.partner;
  let partnerRevealed = state.partnerRevealed ?? false;
  let players = state.players;

  if (
    state.playerCount === 5 &&
    state.calledKing !== undefined &&
    cardPlayed === state.calledKing
  ) {
    partner = cardPlayer;
    partnerRevealed = true;
    players = state.players.map((p, i) => ({
      ...p,
      isPartner: i === cardPlayer,
    }));
    log.debug(`[GameReducer] Partner revealed: player ${cardPlayer}`);
  }

  return {
    ...state,
    phase: 6, // S'assurer qu'on est en phase de jeu
    currentTrick: newTrick,
    trickPlayerPositions: newPositions,
    entameur,
    currentPlayer: nextPlayer,
    myHand: newHand,
    trickComplete: trickIsComplete,
    partner,
    partnerRevealed,
    players,
  };
}

/**
 * Traite la fin d'un pli
 */
function handleFinPli(state: GameState, data: FinPliData): GameState {
  const trickWinner = Number(data.gagnant);
  
  log.debug(`[GameReducer] ===== FIN_PLI =====`);
  log.debug(`[GameReducer] Winner: ${trickWinner}`);
  log.debug(`[GameReducer] Current trick: ${state.currentTrick.length} cards`);
  log.debug(`[GameReducer] Current numPli: ${state.numPli}`);
  
  // Si le pli est déjà vide, ne rien faire (éviter les doublons)
  if (state.currentTrick.length === 0) {
    log.debug('[GameReducer] Trick already empty, skipping');
    return state;
  }
  
  // Mettre à jour le compteur de plis gagnés
  const newPlayers = [...state.players];
  if (newPlayers[trickWinner]) {
    newPlayers[trickWinner] = {
      ...newPlayers[trickWinner],
      tricksWon: (newPlayers[trickWinner].tricksWon || 0) + 1,
    };
  }
  
  const newState = {
    ...state,
    players: newPlayers,
    lastTrick: state.currentTrick,
    currentTrick: [],
    trickPlayerPositions: [],
    entameur: trickWinner,
    currentPlayer: trickWinner,
    numPli: state.numPli + 1,
    trickComplete: false, // Réinitialiser le flag
  };
  
  log.debug(`[GameReducer] New numPli: ${newState.numPli}, new currentPlayer: ${newState.currentPlayer}`);
  log.debug(`[GameReducer] ===================`);
  
  return newState;
}

/**
 * Traite une enchère
 * 
 * Note: cette action enregistre l'enchère et met à jour taker/currentContract,
 * mais NE change PAS currentPlayer ni la phase.
 * currentPlayer reste sur l'enchérisseur pour permettre l'affichage de son enchère.
 * L'action 'bid_advance' (traitée après un délai d'affichage) se charge d'avancer
 * currentPlayer et de changer la phase si nécessaire.
 */
function handleContrat(state: GameState, data: ContratData): GameState {
  const bidPlayer = Number(data.joueur);
  const bidValue = Number(data.contrat);
  
  log.debug(`[GameReducer] Bid: player=${bidPlayer}, value=${bidValue}, dealer=${state.dealer}, phase=${state.phase}`);
  
  // Mettre à jour le joueur
  const newPlayers = [...state.players];
  if (newPlayers[bidPlayer]) {
    newPlayers[bidPlayer] = {
      ...newPlayers[bidPlayer],
      lastBid: bidValue as Contract,
    };
  }
  
  // Déterminer le nouveau preneur et contrat
  let taker = state.taker;
  let currentContract = state.currentContract || 0;
  
  if (bidValue > currentContract) {
    taker = bidPlayer;
    currentContract = bidValue;
  }
  
  log.debug(`[GameReducer] Bid recorded: bidPlayer=${bidPlayer}, taker=${taker}, currentContract=${currentContract}`);
  
  return {
    ...state,
    players: newPlayers,
    currentPlayer: bidPlayer,  // Reste sur l'enchérisseur (bid_advance avancera)
    taker,
    currentContract: currentContract as Contract,
    // phase inchangée (bid_advance la changera si nécessaire)
  };
}

/**
 * Avance au joueur suivant après l'affichage d'une enchère.
 * 
 * Si le donneur a parlé, détermine la phase suivante :
 * - Quelqu'un a pris : phase 4 (Chien) ou 6 (Jeu direct pour Garde Sans/Contre)
 * - Tout le monde a passé : reste en phase 2, attend le message 'chien'
 */
function handleBidAdvance(state: GameState): GameState {
  const bidPlayer = state.currentPlayer;
  const nextBidder = (bidPlayer + 1) % state.playerCount;
  const dealerHasSpoken = bidPlayer === state.dealer;
  const currentContract = state.currentContract || 0;
  
  let phase = state.phase;
  
  log.debug(`[GameReducer] Bid advance: bidPlayer=${bidPlayer}, dealer=${state.dealer}, dealerHasSpoken=${dealerHasSpoken}, currentContract=${currentContract}`);
  
  if (dealerHasSpoken && currentContract > 0) {
    if (state.playerCount === 5) {
      phase = 3; // Appel du roi avant chien / jeu
      log.debug('[GameReducer] 5 players -> phase 3 (King call)');
    } else if (currentContract >= 3) {
      phase = 6; // Garde sans/contre -> jeu direct
      log.debug('[GameReducer] Garde Sans/Contre -> phase 6 (Playing)');
    } else {
      phase = 4; // Chien
      log.debug('[GameReducer] Someone took -> phase 4 (Chien)');
    }
  }
  // Si tout le monde a passé (currentContract === 0), on reste en phase 2
  // et on attend le message 'chien' qui passera en phase 4
  
  return {
    ...state,
    currentPlayer: dealerHasSpoken ? (state.taker ?? 0) : nextBidder,
    phase,
  };
}

/**
 * Traite l'appel du roi (5 joueurs)
 */
function handleAppel(state: GameState, data: AppelData): GameState {
  const calledKing = resolveCalledCard(data);
  log.debug('[GameReducer] King/queen called:', calledKing);

  const players = state.players.map(p => ({
    ...p,
    isPartner: false,
  }));

  const takerPos = state.taker ?? state.currentPlayer;
  const isDefender = state.myPosition !== takerPos;

  return {
    ...state,
    phase: 3,
    calledKing,
    partner: undefined,
    partnerRevealed: false,
    kingCallRevealPending: state.playerCount === 5 && isDefender,
    players,
    currentPlayer: state.taker ?? state.currentPlayer,
  };
}

/**
 * Traite la mise à jour des joueurs
 */
function handleJoueurs(state: GameState, data: { joueurs: Array<{ pseudo: string; position: number; connecte?: boolean }> }): GameState {
  const players: Player[] = [...data.joueurs].sort((a, b) => a.position - b.position).map(j => ({
    name: j.pseudo,
    position: j.position,
    isConnected: j.connecte !== false,
    tricksWon: state.players[j.position]?.tricksWon,
    lastBid: state.players[j.position]?.lastBid,
  }));
  
  return {
    ...state,
    players,
    playerCount: players.length,
  };
}

/**
 * Traite les informations de la table (pseudos des joueurs)
 */
function handleInfosTable(state: GameState, data: { pseudos?: string[]; commentaire?: string; position?: number; connectes?: string[]; tempsLimite?: (string | number)[] }): GameState {
  if (!data.pseudos) return state;
  
  // Calculer si d'autres humains sont présents
  // connectes = ['1', '', '', ''] où '1' = humain connecté, '' = bot
  // On compte le nombre de '1' : s'il y en a plus de 1, il y a d'autres humains
  const humanCount = data.connectes?.filter(c => c === '1').length ?? 0;
  const hasOtherHumans = humanCount > 1;
  
  const players: Player[] = data.pseudos.map((pseudo, idx) => ({
    name: pseudo.replace(/¶/g, ' '),
    position: idx,
    isConnected: data.connectes ? data.connectes[idx] === '1' : true,
    tricksWon: state.players[idx]?.tricksWon,
    lastBid: state.players[idx]?.lastBid,
  }));
  
  // Parser les temps limites si fournis par le serveur Java
  // tempsLimite = [contrat, appel, ecart, carte, score] en secondes
  let timeLimits = state.timeLimits;
  if (data.tempsLimite && Array.isArray(data.tempsLimite) && data.tempsLimite.length >= 5) {
    const parsed = {
      contrat: parseInt(String(data.tempsLimite[0])) || 0,
      appel: parseInt(String(data.tempsLimite[1])) || 0,
      ecart: parseInt(String(data.tempsLimite[2])) || 0,
      carte: parseInt(String(data.tempsLimite[3])) || 0,
      score: parseInt(String(data.tempsLimite[4])) || 0,
    };
    // Ne stocker que si au moins un temps limite est défini (> 0)
    if (parsed.contrat > 0 || parsed.ecart > 0 || parsed.carte > 0 || parsed.score > 0) {
      timeLimits = parsed;
      log.debug('[GameReducer] Time limits parsed:', timeLimits);
    }
  }
  
  return {
    ...state,
    players,
    playerCount: players.length,
    hasOtherHumans,
    phase: state.phase || 1, // Passer à la phase "Seated" si on était en "Waiting"
    // Si le serveur nous donne explicitement la position, la privilégier
    myPosition: data.position !== undefined ? data.position : state.myPosition,
    timeLimits,
  };
}
